import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config';
import { NEON_PALETTE } from '@/utils/ColorUtils';
import { lerpColor } from '@/utils/ColorUtils';

/**
 * Intense full-screen ambient effects overlay.
 *
 * LASERS: thick glowing beams from multiple origins, sweeping fans,
 *   scanner patterns, crossing beams with bright intersection flares,
 *   color cycling, beat-reactive intensity.
 *
 * FOG: layered ground fog that rises and rolls, beat-reactive density
 *   pulses, laser-tinted color bleeding, volumetric gradient layers.
 *
 * STROBES: multi-flash burst patterns, screen shake, tinted color
 *   flashes, afterimage decay.
 */
export class AmbientOverlay {
  private scene: Phaser.Scene;

  // Graphics layers (separate for blend modes / depth)
  private laserGfx: Phaser.GameObjects.Graphics;
  private laserGlowGfx: Phaser.GameObjects.Graphics;
  private fogGfx: Phaser.GameObjects.Graphics;
  private fogGroundGfx: Phaser.GameObjects.Graphics;
  private strobeColorRect: Phaser.GameObjects.Rectangle;
  private flashRect: Phaser.GameObjects.Rectangle;

  private laserCount = 0;
  private fogCount = 0;
  private strobeCount = 0;
  private beatProgress = 0;
  private beatCount = 0;
  private timeSec = 0;

  // Strobe state for multi-flash
  private strobeFlashQueue: number[] = [];
  private strobeFlashTimer = 0;

  // Camera shake
  private shakeIntensity = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Wide laser glow (drawn first, behind beams)
    this.laserGlowGfx = scene.add.graphics();
    this.laserGlowGfx.setBlendMode(Phaser.BlendModes.ADD);
    this.laserGlowGfx.setDepth(2);
    this.laserGlowGfx.setScrollFactor(0);

    // Laser core beams
    this.laserGfx = scene.add.graphics();
    this.laserGfx.setBlendMode(Phaser.BlendModes.ADD);
    this.laserGfx.setDepth(3);
    this.laserGfx.setScrollFactor(0);

    // Ground fog layer (below everything atmospheric)
    this.fogGroundGfx = scene.add.graphics();
    this.fogGroundGfx.setBlendMode(Phaser.BlendModes.NORMAL);
    this.fogGroundGfx.setDepth(1);
    this.fogGroundGfx.setScrollFactor(0);

    // Upper fog (above most things, below UI)
    this.fogGfx = scene.add.graphics();
    this.fogGfx.setBlendMode(Phaser.BlendModes.ADD);
    this.fogGfx.setDepth(450);
    this.fogGfx.setScrollFactor(0);

    // Strobe layers: colored afterglow + white burst flash
    this.strobeColorRect = scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xff00ff, 0
    );
    this.strobeColorRect.setScrollFactor(0);
    this.strobeColorRect.setBlendMode(Phaser.BlendModes.ADD);
    this.strobeColorRect.setDepth(899);

    // Hard white flash for multi-strobe bursts
    this.flashRect = scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0
    );
    this.flashRect.setScrollFactor(0);
    this.flashRect.setBlendMode(Phaser.BlendModes.ADD);
    this.flashRect.setDepth(901);
  }

  setEquipmentCounts(lasers: number, fog: number, strobes: number): void {
    this.laserCount = lasers;
    this.fogCount = fog;
    this.strobeCount = strobes;
  }

  onBeat(beatCount: number): void {
    this.beatCount = beatCount;

    if (this.strobeCount > 0) {
      // Multi-flash burst: 1 strobe = single flash, 2+ = double, 4+ = triple
      const flashes = this.strobeCount >= 4 ? 3 : this.strobeCount >= 2 ? 2 : 1;
      this.strobeFlashQueue = [];
      for (let i = 0; i < flashes; i++) {
        this.strobeFlashQueue.push(i * 50); // 50ms between sub-flashes
      }
      this.strobeFlashTimer = 0;

      // Tinted afterglow color cycles
      const color = NEON_PALETTE[beatCount % NEON_PALETTE.length];
      this.strobeColorRect.setFillStyle(color);
      const afterAlpha = Math.min(0.25, 0.05 + 0.04 * this.strobeCount);
      this.strobeColorRect.setAlpha(afterAlpha);
      this.scene.tweens.add({
        targets: this.strobeColorRect,
        alpha: 0,
        duration: 300,
        ease: 'Sine.easeOut',
      });

      // Camera shake on beat (scales with strobe count)
      this.shakeIntensity = Math.min(4, 0.5 + this.strobeCount * 0.5);
    }
  }

  update(beatProgress: number): void {
    this.beatProgress = beatProgress;
    this.timeSec = this.scene.time.now / 1000;

    this.processStrobeFlashes();
    this.drawLasers();
    this.drawFog();
    this.applyCameraShake();
  }

  // ─── STROBE MULTI-FLASH ──────────────────────────────────

  private processStrobeFlashes(): void {
    if (this.strobeFlashQueue.length === 0) return;

    this.strobeFlashTimer += this.scene.game.loop.delta;

    while (this.strobeFlashQueue.length > 0 && this.strobeFlashTimer >= this.strobeFlashQueue[0]) {
      this.strobeFlashQueue.shift();
      this.fireStrobeFlash();
    }
  }

  private fireStrobeFlash(): void {
    const intensity = Math.min(0.8, 0.2 + 0.12 * this.strobeCount);
    this.flashRect.setAlpha(intensity);
    this.scene.tweens.add({
      targets: this.flashRect,
      alpha: 0,
      duration: 60,
      ease: 'Power4',
    });
  }

  // ─── CAMERA SHAKE ──────────────────────────────────────

  private applyCameraShake(): void {
    if (this.shakeIntensity <= 0.1) return;
    this.shakeIntensity *= 0.9; // decay
    // Apply via small random offset to all overlay graphics
    const ox = (Math.random() - 0.5) * this.shakeIntensity * 2;
    const oy = (Math.random() - 0.5) * this.shakeIntensity * 2;
    this.laserGfx.setPosition(ox, oy);
    this.laserGlowGfx.setPosition(ox, oy);
    if (this.shakeIntensity <= 0.1) {
      this.laserGfx.setPosition(0, 0);
      this.laserGlowGfx.setPosition(0, 0);
    }
  }

  // ─── LASERS ──────────────────────────────────────────────

  private drawLasers(): void {
    this.laserGfx.clear();
    this.laserGlowGfx.clear();
    if (this.laserCount === 0) return;

    const t = this.timeSec;
    const bp = this.beatProgress;
    const bc = this.beatCount;

    // Beat-reactive intensity: pulse brighter on beat
    const beatPulse = Math.pow(Math.max(0, 1 - bp * 3), 2); // sharp spike at beat start
    const baseAlpha = Math.min(0.5, 0.12 + 0.06 * this.laserCount);
    const alpha = baseAlpha + beatPulse * 0.3;

    // Number of beam origins and total beams
    const origins = Math.min(this.laserCount, 5);
    const beamsPerOrigin = Math.min(2 + this.laserCount, 6);

    // Collect all beam endpoints for intersection detection
    const beams: Array<{ x1: number; y1: number; x2: number; y2: number; color: number }> = [];

    for (let o = 0; o < origins; o++) {
      // Origins spread across top, with some on sides for variety
      let originX: number, originY: number;
      if (o < 3) {
        // Top edge origins
        originX = GAME_WIDTH * (0.15 + (o / Math.max(1, origins - 1)) * 0.7);
        originY = -5;
      } else if (o === 3) {
        // Left side
        originX = -5;
        originY = GAME_HEIGHT * 0.2;
      } else {
        // Right side
        originX = GAME_WIDTH + 5;
        originY = GAME_HEIGHT * 0.2;
      }

      for (let b = 0; b < beamsPerOrigin; b++) {
        const colorIdx = (o * beamsPerOrigin + b + bc) % NEON_PALETTE.length;
        const color = NEON_PALETTE[colorIdx];

        // Complex sweep patterns
        const sweepBase = t * (0.8 + o * 0.3) + o * 1.5;
        const sweepFast = Math.sin(sweepBase + b * 0.9) * 0.6;
        const sweepSlow = Math.sin(t * 0.2 + o * 2.0) * 0.4;
        const beatKick = Math.sin(bp * Math.PI) * 0.15;

        let angle: number;
        if (o < 3) {
          // Top beams: fan downward
          const spread = (b / Math.max(1, beamsPerOrigin - 1)) * Math.PI * 0.5 + Math.PI * 0.25;
          angle = spread + sweepFast + sweepSlow + beatKick;
        } else {
          // Side beams: sweep across
          const spread = (b / Math.max(1, beamsPerOrigin - 1)) * Math.PI * 0.4;
          angle = (o === 3 ? 0 : Math.PI) + spread - Math.PI * 0.2 + sweepFast + beatKick;
        }

        const length = GAME_HEIGHT * 1.4;
        const endX = originX + Math.cos(angle) * length;
        const endY = originY + Math.sin(angle) * length;

        beams.push({ x1: originX, y1: originY, x2: endX, y2: endY, color });

        // ── Draw wide outer glow (6px, low alpha)
        this.laserGlowGfx.lineStyle(8, color, alpha * 0.15);
        this.laserGlowGfx.beginPath();
        this.laserGlowGfx.moveTo(originX, originY);
        this.laserGlowGfx.lineTo(endX, endY);
        this.laserGlowGfx.strokePath();

        // ── Draw medium glow (4px)
        this.laserGlowGfx.lineStyle(4, color, alpha * 0.3);
        this.laserGlowGfx.beginPath();
        this.laserGlowGfx.moveTo(originX, originY);
        this.laserGlowGfx.lineTo(endX, endY);
        this.laserGlowGfx.strokePath();

        // ── Draw colored core (2px)
        this.laserGfx.lineStyle(2, color, alpha * 0.8);
        this.laserGfx.beginPath();
        this.laserGfx.moveTo(originX, originY);
        this.laserGfx.lineTo(endX, endY);
        this.laserGfx.strokePath();

        // ── Draw white-hot center (1px)
        this.laserGfx.lineStyle(1, 0xffffff, alpha * 0.9);
        this.laserGfx.beginPath();
        this.laserGfx.moveTo(originX, originY);
        this.laserGfx.lineTo(endX, endY);
        this.laserGfx.strokePath();
      }
    }

    // ── Draw intersection flares where beams cross
    this.drawBeamIntersections(beams, alpha);

    // ── Scanner line effect (rapid horizontal sweep on every other beat)
    if (this.laserCount >= 2 && bc % 2 === 0) {
      this.drawScannerSweep(alpha, bp);
    }
  }

  private drawBeamIntersections(
    beams: Array<{ x1: number; y1: number; x2: number; y2: number; color: number }>,
    alpha: number
  ): void {
    // Check pairs for intersections (limit to avoid O(n^2) explosion)
    const maxChecks = Math.min(beams.length, 12);
    for (let i = 0; i < maxChecks; i++) {
      for (let j = i + 1; j < maxChecks; j++) {
        const pt = lineIntersection(beams[i], beams[j]);
        if (pt && pt.x > -50 && pt.x < GAME_WIDTH + 50 && pt.y > -50 && pt.y < GAME_HEIGHT + 50) {
          // Bright flare at intersection
          const flareColor = lerpColor(beams[i].color, beams[j].color, 0.5);
          this.laserGfx.fillStyle(0xffffff, alpha * 0.6);
          this.laserGfx.fillCircle(pt.x, pt.y, 4);
          this.laserGfx.fillStyle(flareColor, alpha * 0.4);
          this.laserGfx.fillCircle(pt.x, pt.y, 8);
          this.laserGlowGfx.fillStyle(flareColor, alpha * 0.15);
          this.laserGlowGfx.fillCircle(pt.x, pt.y, 18);
        }
      }
    }
  }

  private drawScannerSweep(alpha: number, bp: number): void {
    // Horizontal laser line that sweeps top-to-bottom during the beat
    const scanY = bp * GAME_HEIGHT;
    const scanColor = NEON_PALETTE[this.beatCount % NEON_PALETTE.length];
    const scanAlpha = alpha * Math.pow(1 - bp, 2) * 0.6;

    this.laserGfx.lineStyle(2, scanColor, scanAlpha);
    this.laserGfx.beginPath();
    this.laserGfx.moveTo(0, scanY);
    this.laserGfx.lineTo(GAME_WIDTH, scanY);
    this.laserGfx.strokePath();

    this.laserGlowGfx.lineStyle(8, scanColor, scanAlpha * 0.3);
    this.laserGlowGfx.beginPath();
    this.laserGlowGfx.moveTo(0, scanY);
    this.laserGlowGfx.lineTo(GAME_WIDTH, scanY);
    this.laserGlowGfx.strokePath();
  }

  // ─── FOG ──────────────────────────────────────────────────

  private drawFog(): void {
    this.fogGfx.clear();
    this.fogGroundGfx.clear();
    if (this.fogCount === 0) return;

    const t = this.timeSec;
    const bp = this.beatProgress;

    // Beat-reactive: fog density pulses on beat
    const beatPulse = Math.pow(Math.max(0, 1 - bp * 4), 3) * 0.4;
    const density = Math.min(1, 0.15 + 0.12 * this.fogCount) + beatPulse;

    // ── Ground fog: thick haze rising from the bottom
    this.drawGroundFog(t, density);

    // ── Upper atmospheric haze
    this.drawAtmosphericFog(t, density);

    // ── Laser-tinted fog wisps (if we have both fog + lasers)
    if (this.laserCount > 0) {
      this.drawLaserTintedFog(t, density);
    }
  }

  private drawGroundFog(t: number, density: number): void {
    // Multiple gradient bands from bottom, with wave distortion
    const layers = Math.min(3 + this.fogCount, 8);

    for (let layer = 0; layer < layers; layer++) {
      const layerT = layer / layers;
      // Each layer is a horizontal band that undulates
      const baseY = GAME_HEIGHT - (layerT * GAME_HEIGHT * 0.5);
      const waveAmp = 15 + layer * 5;
      const alpha = density * (1 - layerT * 0.7) * 0.08;

      // Fog tint: bottom layers are cool grey, upper layers pick up color
      const fogColor = layer > layers * 0.6
        ? lerpColor(0x445566, NEON_PALETTE[this.beatCount % NEON_PALETTE.length], 0.15)
        : 0x445566;

      this.fogGroundGfx.fillStyle(fogColor, alpha);

      // Draw wavy band using filled polygon
      this.fogGroundGfx.beginPath();
      this.fogGroundGfx.moveTo(-10, GAME_HEIGHT + 10);
      for (let x = -10; x <= GAME_WIDTH + 10; x += 15) {
        const wave = Math.sin(x * 0.005 + t * (0.3 + layer * 0.1) + layer * 1.5) * waveAmp
                   + Math.sin(x * 0.01 + t * 0.7 + layer) * waveAmp * 0.5;
        this.fogGroundGfx.lineTo(x, baseY + wave);
      }
      this.fogGroundGfx.lineTo(GAME_WIDTH + 10, GAME_HEIGHT + 10);
      this.fogGroundGfx.closePath();
      this.fogGroundGfx.fillPath();
    }
  }

  private drawAtmosphericFog(t: number, density: number): void {
    // Floating fog wisps in the upper portion of the screen
    const wispCount = Math.min(this.fogCount * 2, 8);
    const alpha = density * 0.06;

    for (let i = 0; i < wispCount; i++) {
      const speed = 0.15 + i * 0.08;
      const yBase = GAME_HEIGHT * (0.2 + (i / wispCount) * 0.4);
      const driftX = Math.sin(t * speed + i * 2.5) * 100;
      const driftY = Math.sin(t * speed * 0.7 + i * 1.3) * 30;

      // Each wisp is a cluster of overlapping ellipses
      for (let e = 0; e < 3; e++) {
        const ex = (t * (15 + i * 8) + i * 200 + e * 150 + driftX) % (GAME_WIDTH + 500) - 250;
        const ey = yBase + driftY + e * 15;
        const w = 200 + i * 30 + Math.sin(t + e) * 40;
        const h = 50 + i * 10;

        this.fogGfx.fillStyle(0x8899bb, alpha * (1 - e * 0.2));
        this.fogGfx.fillEllipse(ex, ey, w, h);
      }
    }
  }

  private drawLaserTintedFog(t: number, density: number): void {
    // Colored fog patches that look like laser light scattering in fog
    const patches = Math.min(this.laserCount * 2, 6);
    const alpha = density * 0.05;

    for (let i = 0; i < patches; i++) {
      const color = NEON_PALETTE[(i + this.beatCount) % NEON_PALETTE.length];
      const x = (t * (10 + i * 5) + i * 300) % (GAME_WIDTH + 300) - 150;
      const y = GAME_HEIGHT * (0.3 + Math.sin(t * 0.3 + i) * 0.2);
      const size = 120 + Math.sin(t * 0.5 + i * 2) * 40;

      this.fogGfx.fillStyle(color, alpha);
      this.fogGfx.fillEllipse(x, y, size, size * 0.6);
    }
  }

  // ─── CLEANUP ──────────────────────────────────────────────

  destroy(): void {
    this.laserGfx.destroy();
    this.laserGlowGfx.destroy();
    this.fogGfx.destroy();
    this.fogGroundGfx.destroy();
    this.strobeColorRect.destroy();
    this.flashRect.destroy();
  }
}

// ─── MATH HELPERS ──────────────────────────────────────────

/** Find intersection point of two line segments, or null if they don't cross */
function lineIntersection(
  a: { x1: number; y1: number; x2: number; y2: number },
  b: { x1: number; y1: number; x2: number; y2: number }
): { x: number; y: number } | null {
  const dx1 = a.x2 - a.x1, dy1 = a.y2 - a.y1;
  const dx2 = b.x2 - b.x1, dy2 = b.y2 - b.y1;
  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 0.001) return null; // parallel

  const t = ((b.x1 - a.x1) * dy2 - (b.y1 - a.y1) * dx2) / denom;
  const u = ((b.x1 - a.x1) * dy1 - (b.y1 - a.y1) * dx1) / denom;

  if (t < 0 || t > 1 || u < 0 || u > 1) return null; // outside segments

  return { x: a.x1 + t * dx1, y: a.y1 + t * dy1 };
}
